import { css, html } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { styleMap } from 'lit/directives/style-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import { useEffect } from 'haunted'
import { debounce } from 'lodash'
import { LitHauntedElement } from '../../mixins/lit-haunted-element';
import type { Combobox as FluentCombobox } from '@fluentui/web-components/dist/dts/combobox/index'

const DEBUG: boolean = false

type Option = { value: string, label: string, disabled: boolean };

/**
 * A segmented picker element element
 */
@customElement('ep-select-searchable')
export class EPSelectSearchable extends LitHauntedElement {
  @state()
  searchQuery: string = ''

  @state()
  isLoading: boolean = false;

  @state()
  options: Option[] = [];

  @state()
  filteredOptions: Option[] = [];

  @property({ type: Number, attribute: 'limit', reflect: true })
  resultLimit: number = 100

  //#region Select Element mapping
  @queryAssignedElements({ slot: 'select', selector: 'select' })
  selectElements?: HTMLSelectElement[];
  get selectElement() { return this.selectElements?.[0]; }
  get value(): string {
    return this.comboboxRef.value?.firstSelectedOption?.value ?? '';
  }
  set value(newVal: string) {
    // TODO: <fluent-combobox> is a MESS
  }
  //#endregion

  comboboxRef: Ref<FluentCombobox> = createRef();
  private _handleInput: () => void = debounce(() => {
    if (this.comboboxRef.value) {
      const value = this.comboboxRef.value.control.value;
      //console.log('value', value)
      if (this.searchQuery === value) this.isLoading = false;
      this.searchQuery = value;
    }
  }, 250, { leading: true, trailing: true });

  private _handleChange() {
    // Must be an option that was selected
    const value = this.comboboxRef.value?.firstSelectedOption?.value;
    //console.log('change', value);
    if (value !== undefined && this.selectElement) {
      for(let option of this.selectElement.options) {
        if (option.value === value) {
          option.selected = true;
          break;
        }
      }
      const event = new CustomEvent('change')
      this.selectElement.dispatchEvent(event);
    }
  }

  render() {
    useEffect(() => {
      // Don't update multiple times
      if (this.selectElement && this.options.length === 0) {
        //console.log('select element', this.selectElement)
        this.options = Array.from(this.selectElement?.options ?? [])
          .map(opt => ({ value: opt.value, label: opt.label, disabled: opt.disabled }));
        // whatever is already selected, pre-select it
        const selectedOption = this.selectElement.selectedOptions[0];
        if (selectedOption && this.comboboxRef.value) {
          // combobox uses label instead of value ...
          //console.log('selectedOption?', selectedOption);
          this.searchQuery = selectedOption.label
          setTimeout(() => {
            if (this.comboboxRef.value)
              this.comboboxRef.value.value = selectedOption.label
          }, 0);
        }
      }
      if (this.selectElement === undefined) {
        this.style.display = 'none'
      }
    }, [this.selectElement])

    useEffect(() => {
      this.filteredOptions = this.options
        .filter(opt => 
          opt.value.toLowerCase().includes(this.searchQuery.toLowerCase())
          || opt.label.toLowerCase().includes(this.searchQuery.toLowerCase())
          )
        .slice(0, this.resultLimit);
      this.isLoading = false;
    }, [this.searchQuery])

    return html`
      <slot name="select"
        style=${styleMap({ 'display': DEBUG ? '' : 'none' })}
        >
      </slot>
      <fluent-combobox
        ${ref(this.comboboxRef)}
        autocomplete="none"
        placeholder="Choose an item"
        .value=${this.searchQuery}
        @input=${() => {
          this.isLoading = true;
          this._handleInput();
        }}
        @change=${this._handleChange}
        style="--max-height: 350px;"
      >
        ${
          this.isLoading
          ? html`<fluent-progress-ring slot="start" style="width: 20px; height: 20px;"></fluent-progress-ring>`
          : html`<span slot="start">🔎</span>`
        }
        ${this.filteredOptions.map(opt => (
          html`
          <fluent-option value=${opt.value} title=${opt.label} ?disabled=${opt.disabled}>
            ${opt.label}
          </fluent-option>
          `
        ))}
        ${this.filteredOptions.length < this.options.length ? html`
        <fluent-option class="searchable" disabled>
          <span>${this.resultLimit === this.filteredOptions.length ? 'Limited to ' : ''}${this.filteredOptions.length.toLocaleString()} results, ${(this.options.length - this.filteredOptions.length).toLocaleString()} omitted</span>
        </fluent-option>
        ` : ''}
      </fluent-combobox>
    `
  }

  static styles = css`
    :host {
      display: contents;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
      line-height: 1.5;
      color: rgb(36, 41, 47);
      --row-height: 32px;
      --animation-duration: 150ms;

      --max-height: 320px;
      --max-width: 256px;
    }

    .searchable {
      font-size: 12px;
      font-weight: 600;
      font-style: italic;
    }

    * {
      box-sizing: border-box;
    }

    ep-nav-list-box {
      min-width: 192px;
      max-width: var(--max-width);
      //max-height: var(--max-height);
      height: auto;
    }

    [part=panel-header] {
      padding: 8px 16px 0;
    }

    [part=panel-header] h1 {
      font-weight: 600;
      margin: 0px;
      font-size: 14px;
    }

    [part=title], ::slotted([slot=title]) {
      font-weight: 600;
      margin: 0px;
      font-size: 14px;
    }

    [part=subtitle], ::slotted([slot=subtitle]) {
      font-weight: 400;
      margin: 0px;
      font-size: 12px;
      color: rgb(101, 109, 118);
    }

    [part=filter-list] {
      display: flex;
      overflow: hidden;
      flex-direction: column;
      height: auto;
      //max-height: var(--max-height);
    }

    [part=list] {
      max-height: var(--max-height);
      overflow-y: auto;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-select-searchable': EPSelectSearchable,
  }
}
